import React, { forwardRef } from "react";
import { Box, Text, UnstyledButton } from "@mantine/core";
import type { Icon } from "@tabler/icons-react";
import styles from "./NavRail.module.css";

export interface RailButtonProps extends Omit<
  React.ComponentPropsWithoutRef<"button">,
  "color"
> {
  icon: Icon;
  /** Short label under the icon (also the accessible name). */
  label: string;
  /** The current page lives here (compact active pill). */
  active?: boolean;
  /** Its flyout / popover is open. */
  lit?: boolean;
  /** Unread count shown as a red badge on the icon. */
  badge?: number;
}

/**
 * One 56px button in the nav rail: icon over a short label. Used for the
 * sections and the utilities (Alerts, Chat). Forwards its ref so Mantine
 * Popover / Menu targets can wrap it.
 */
export const RailButton = forwardRef<HTMLButtonElement, RailButtonProps>(
  (
    { icon: IconCmp, label, active, lit, badge = 0, className, ...rest },
    ref,
  ) => (
    <UnstyledButton
      ref={ref}
      // Merged: Popover / Menu targets inject their own className.
      className={[styles.railButton, className].filter(Boolean).join(" ")}
      data-active={active || undefined}
      data-lit={lit || undefined}
      aria-label={badge > 0 ? `${label}, ${badge} unread` : label}
      aria-current={active ? "page" : undefined}
      {...rest}
    >
      <Box component="span" className={styles.railIcon}>
        <IconCmp size={20} stroke={1.75} />
        {badge > 0 && (
          <Text span className={styles.railBadge}>
            {badge > 99 ? "99+" : badge}
          </Text>
        )}
      </Box>
      <Text span className={styles.railLabel}>
        {label}
      </Text>
    </UnstyledButton>
  ),
);

RailButton.displayName = "RailButton";
